
import { useState, useRef, useEffect } from 'react';
import { Search, X, ArrowLeft, Trash2, Camera, SwitchCamera, LoaderCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGlobalState } from '../../utils/GlobalState';
import { predictIssueDetection } from '../../services/apiService';
import { decodedToken } from '../../utils/auth';

const formatRootCauseName = (key) => {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const UploadImage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateState, state: globalState } = useGlobalState();

  const [imageSrc, setImageSrc] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraFacingMode, setCameraFacingMode] = useState('environment');
  const [loading, setLoading] = useState(false);
  const [analysisInProgress, setAnalysisInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [previewImageSrc, setPreviewImageSrc] = useState(null);
  const [isDeleted, setIsDeleted] = useState(false);
  const [predictedImageLoaded, setPredictedImageLoaded] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const capturedFileRef = useRef(null);
  const isInitialMount = useRef(true);

  const resetAllStates = () => {
    setImageSrc(null);
    setShowAnalysis(false);
    setAnalysisResult(null);
    setPredictedImageLoaded(false);
    setErrorMessage('');
    setShowSearchResults(false);
    setIsModalOpen(false);
    setLoading(false);
    setIsDeleted(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    capturedFileRef.current = null;
    updateState({
      uploadedImage: null,
      rawImageFile: null,
      predictions: [],
      suggestion: '',
      sentToEmail: '',
      showAnalysis: false,
      analysisResult: null,
      predictedImageUrl: '',
    });
  };

  useEffect(() => {
    if (isInitialMount.current) {
      if (location.state?.fromSubmission) {
        resetAllStates();
      } else {
        setImageSrc(globalState.uploadedImage || null);
        setShowAnalysis(globalState.showAnalysis || false);
        setAnalysisResult(globalState.analysisResult || null);
        capturedFileRef.current = globalState.rawImageFile || null;
        setIsDeleted(false);
      }
      isInitialMount.current = false;
    }
  }, []); 


  useEffect(() => {
    if (showAnalysis && analysisResult?.data?.predicted_image_url) {
      setPredictedImageLoaded(false);
      const img = new Image();
      img.src = analysisResult.data.predicted_image_url;
      img.onload = () => {
        setPredictedImageLoaded(true);
      };
      img.onerror = () => {
        setErrorMessage('Failed to load predicted image.');
        setPredictedImageLoaded(true);
        console.error('Failed to load predicted image:', analysisResult.data.predicted_image_url);
      };
    }
  }, [showAnalysis, analysisResult]);

  const handleApplyAnalysis = async () => {
    let fileToSend = null;
    if (fileInputRef.current && fileInputRef.current.files.length > 0) {
      fileToSend = fileInputRef.current.files[0];
    } else if (capturedFileRef.current) {
      fileToSend = capturedFileRef.current;
    }

    if (fileToSend) {
      setLoading(true);
      setAnalysisInProgress(true);
      setErrorMessage('');
      setShowAnalysis(false);
      setPredictedImageLoaded(false);
      setAnalysisResult(null);

      try {
        const decoded = decodedToken();
        const orgName = decoded?.companyInfo || 'Unknown';
        const uploadedBy = decoded?.email || 'user@example.com';

        const result = await predictIssueDetection({
          imageFile: fileToSend,
          orgName,
          uploadedBy,
        });
        const predictions = result.data?.predictions || [];
        setAnalysisResult(result);
        setShowAnalysis(true);
        setIsDeleted(false);
        const newState = {
          uploadedImage: imageSrc,
          predictions: predictions,
          rawImageFile: fileToSend,
          showAnalysis: true,
          analysisResult: result,
          predictedImageUrl: result.data?.predicted_image_url || '',
          suggestion: globalState.suggestion || '',
          sentToEmail: globalState.sentToEmail || '',
        };
        updateState(newState);
      } catch (error) {
        console.error('Error during analysis:', error.message, error.response?.data);
        setErrorMessage(error.message || 'Failed to fetch prediction');
      } finally {
        setLoading(false);
        setAnalysisInProgress(false);
      }
    } else {
      setErrorMessage('No image file selected or captured for analysis');
    }
  };

 const handleFeedbackClick = () => {
  const enrichedPredictions = (analysisResult?.data?.predictions || []).map(pred => ({
    ...pred,
    predictedImageUrl: analysisResult?.data?.predicted_image_url || '',
  }));
  navigate('/agree-judgement', {
    state: {
      imageSrc,
      showAnalysis,
      analysisResult,
      predictions: enrichedPredictions,
      rawImageFile: capturedFileRef.current || fileInputRef.current?.files[0] || null,
      suggestion: globalState.suggestion || '',
      sentToEmail: globalState.sentToEmail || '',
      predictedImageUrl: analysisResult?.data?.predicted_image_url || '',
    },
  });
};

  const DisagreeJudgementclick = () => {
    const mappedPredictions = (analysisResult?.data?.predictions || []).map(pred => ({
      issueId: pred.class_id.toString(),
      issueType: pred.class_name,
      rootCauseType: '',
      issueDetail: '',
      confidence: pred.confidence,
      bbox: pred.bbox,
      predictedImageUrl: analysisResult?.data?.predicted_image_url || '',
    }));

    navigate('/disagree-judgement', {
      state: {
        imageSrc,
        showAnalysis,
        analysisResult,
        predictions: mappedPredictions,
        uploadedImage: imageSrc,
        rawImageFile: capturedFileRef.current || fileInputRef.current?.files[0] || null,
        suggestion: globalState.suggestion || '',
        sentToEmail: globalState.sentToEmail || '',
        predictedImageUrl: analysisResult?.data?.predicted_image_url || '',
      },
    });
  };

  const handleUploadPhoto = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; 
      fileInputRef.current.click();
    }
  };


  const handleCapturePhoto = () => {
    if (videoRef.current && streamRef.current) {
      const canvas = document.createElement('canvas');
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          const file = new File([blob], 'captured_photo.jpg', { type: 'image/jpeg' });
          capturedFileRef.current = file;
          const dataUrl = canvas.toDataURL('image/jpeg');
          setImageSrc(dataUrl);
          setShowAnalysis(false);
          setShowSearchResults(false);
          setAnalysisResult(null);
          setPredictedImageLoaded(false);
          setErrorMessage('');
          setIsDeleted(false);
          const newState = {
            uploadedImage: dataUrl,
            rawImageFile: file,
            predictions: [],
            suggestion: '',
            sentToEmail: '',
            showAnalysis: false,
            analysisResult: null,
            predictedImageUrl: '',
          };
          updateState(newState);
        },
        'image/jpeg',
        0.7
      );

      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setShowCameraModal(false);
    }
  };

  const handleSwitchCamera = async () => {
    const newFacingMode = cameraFacingMode === 'environment' ? 'user' : 'environment';
    setCameraFacingMode(newFacingMode);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: newFacingMode },
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      } catch (error) {
        console.error('Error switching camera:', error);
      }
    }
  };

  const handleCloseCameraModal = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setShowCameraModal(false);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Please select a valid image file (e.g., JPEG, PNG).');
        return;
      }

      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target.result;
        setImageSrc(dataUrl);
        setShowAnalysis(false);
        setShowSearchResults(false);
        setAnalysisResult(null);
        setPredictedImageLoaded(false);
        setErrorMessage('');
        setIsDeleted(false);
        capturedFileRef.current = file;
        if (fileInputRef.current) fileInputRef.current.value = ''; 
        const newState = {
          uploadedImage: dataUrl,
          rawImageFile: file,
          predictions: [],
          suggestion: '',
          sentToEmail: '',
          showAnalysis: false,
          analysisResult: null,
          predictedImageUrl: '',
        };
        updateState(newState);
      };
      reader.onerror = () => {
        setErrorMessage('Failed to read the image file.');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = (imageType) => {
    if (imageSrc) {
      setPreviewImageSrc(imageType === 'original' ? imageSrc : analysisResult?.data?.predicted_image_url);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDeleteImage = () => {
    resetAllStates();
  };

  const toggleSearchResults = () => {
    setShowSearchResults(!showSearchResults);
  };

  const handleCloseSearchResults = () => {
    setShowSearchResults(false);
  };

  const handleBackToDashboard = () => {
    resetAllStates();
    navigate('/productinfodetection');
  };

  const getRootCausesArray = () => {
    if (analysisResult?.percentages?.rootCauses) {
      return Object.entries(analysisResult.percentages.rootCauses).map(([key, value]) => ({
        name: formatRootCauseName(key),
        percentage: value.percentage,
      }));
    }
    return [];
  };

  return (
    <div className="w-full min-h-screen bg-gray-900 text-white flex items-center justify-center p-2 sm:p-4">
      <div className="w-full max-w-4xl bg-gray-900 rounded-lg shadow-md p-4 sm:p-6 flex flex-col gap-6">
        <div className="flex items-center justify-between w-full mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackToDashboard}
              className="p-2 bg-gray-800 text-white rounded-full hover:bg-gray-700 transition"
              title="Back to Dashboard"
              aria-label="Back to Dashboard"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold">LL/Mura/Spot/Band Issue Detection</h1>
          </div>
        </div>

        <div className="w-full max-w-2xl mx-auto" key={imageSrc || 'no-image'}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm sm:text-base text-white">Original Photo</h2>
            {imageSrc && (
              <button
                onClick={handleDeleteImage}
                className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                title="Delete Uploaded Image"
                aria-label="Delete Uploaded Image"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          {imageSrc ? (
            <img
              src={imageSrc}
              alt="Uploaded"
              className="w-full h-64 sm:h-80 md:h-96 lg:h-[420px] object-cover rounded-lg cursor-pointer"
              onClick={() => handleImageClick('original')}
            />
          ) : (
            <div className="w-full h-64 sm:h-80 md:h-96 lg:h-[420px] bg-gray-800 flex items-center justify-center">
              <span className="text-gray-400 text-sm sm:text-base">No Image Selected</span>
            </div>
          )}
        </div>
        <div className="w-full max-w-2xl mx-auto flex justify-center gap-4">
          <button
            onClick={handleUploadPhoto}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm disabled:bg-gray-500 disabled:cursor-not-allowed"
            disabled={analysisInProgress}
          >
            Upload Photo
          </button>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />
          <input
            type="file"
            accept="image/*"
            capture={cameraFacingMode}
            ref={cameraInputRef}
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            onClick={handleApplyAnalysis}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm disabled:bg-gray-500 disabled:cursor-not-allowed"
            disabled={!imageSrc || analysisInProgress || showAnalysis}
            aria-label={imageSrc ? 'Analyze the uploaded image' : 'Analyze button disabled, please upload an image'}
          >
            Analyze
          </button>
        </div>

        {/* {(analysisInProgress || showAnalysis) && (
          <div className="w-full">
            <div className="w-full max-w-2xl mx-auto mb-6">
              <h2 className="text-sm sm:text-base mb-2 text-white">Detection Result</h2>
              {(loading || !predictedImageLoaded) ? (
                <div className="w-full h-64 sm:h-80 md:h-96 lg:h-[420px] bg-gray-800 flex items-center justify-center">
                  <LoaderCircle className="w-8 h-8 text-blue-500 animate-spin" />
                </div>
              ) : showAnalysis && analysisResult?.data?.predicted_image_url ? (
                <img
                  src={analysisResult.data.predicted_image_url}
                  alt="Predicted"
                  className="w-full h-64 sm:h-80 md:h-96 lg:h-[420px] object-cover cursor-pointer"
                  onClick={() => handleImageClick('analyzed')}
                />
              ) : (
                <div className="w-full h-64 sm:h-80 md:h-96 lg:h-[420px] bg-gray-800 flex items-center justify-center">
                  <span className="text-gray-400 text-sm sm:text-base">No Analysis Available</span>
                </div>
              )}
            </div>
            <div className="border-t border-gray-700 pt-6">
              {showAnalysis && analysisResult?.data?.predictions?.length > 0 && (
                <div className="flex items-center mb-4 w-full max-w-2xl mx-auto">
                  <button
                    onClick={toggleSearchResults}
                    className="flex items-center hover:bg-gray-800 p-2 rounded transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!analysisResult?.data?.predictions?.length}
                  >
                    <h2 className="text-lg sm:text-xl md:text-2xl font-semibold flex-1 text-left">
                      View Defect Risk Factor Details
                    </h2>
                    <Search className="w-7 h-7 text-white-500 ml-3" />
                  </button>
                </div>
              )}
              {(loading || showAnalysis) && (
                <div className="w-full max-w-2xl mx-auto p-4 bg-gray-900 rounded-lg min-h-[150px] sm:min-h-[200px]">
                  <div className="mt-5 text-gray-300">
                    {analysisResult && (
                      <>
                        {Array.isArray(analysisResult.data?.predictions) && analysisResult.data.predictions.length > 0 ? (
                          <>
                            <ul className="list-disc pl-5">
                              {analysisResult.data.predictions.map((pred, index) => (
                                <li key={index} className="text-gray-300">
                                  <span className="font-medium">The Most Likely Type of Issue:</span>{' '}
                                  {pred.class_name}
                                </li>
                              ))}
                            </ul>
                            <div className="mt-5 text-gray-300">
                              <p>
                                To view detailed possible contributing factors, please click the "View Defect Risk Factor Details" button above. We
                                would appreciate your feedback on any incorrect judgments. For any detected light
                                leakage issue, please refer to the PSS or contact the corresponding TPV
                                representative for criteria confirmation. Note: Light leakage is not acceptable on
                                MNT products.
                              </p>
                            </div>
                          </>
                        ) : (
                          <div className="text-green-500 text-sm sm:text-base">
                            <p>✅ No issue found in the image.</p>
                            <p className="mt-2">
                              The status means the result is good, or that there were not enough error patterns to make a clear judgment.If you believe an issue was missed, please press the "Disagree Judgment" button, leave your comments, and re-label the photo to help us improve the system's performance.
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              {showAnalysis && (
                <div className="flex flex-col items-center gap-3 mt-6 w-full max-w-2xl mx-auto">
                  <button
                    onClick={handleFeedbackClick}
                    className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition text-sm w-full max-w-md"
                    disabled={!imageSrc}
                  >
                    Agree Judgment and E-mail Result
                  </button>
                  <button
                    onClick={DisagreeJudgementclick}
                    className="bg-gray-300 text-black px-4 py-2 rounded-lg hover:bg-gray-400 transition text-sm w-full max-w-md"
                    disabled={!showAnalysis}
                  >
                    Disagree Judgment
                  </button>
                </div>
              )}
            </div>
          </div>
        )} */}

        {(analysisInProgress || showAnalysis) && (
          <div className="w-full">
            <div className="w-full max-w-2xl mx-auto mb-6">
              <h2 className="text-sm sm:text-base mb-2 text-white">Detection Result</h2>
              {(loading || !predictedImageLoaded) ? (
                <div className="w-full h-64 sm:h-80 md:h-96 lg:h-[420px] bg-gray-800 flex items-center justify-center">
                  <LoaderCircle className="w-8 h-8 text-blue-500 animate-spin" />
                </div>
              ) : showAnalysis && analysisResult?.data?.predicted_image_url ? (
                <img
                  src={analysisResult.data.predicted_image_url}
                  alt="Predicted"
                  className="w-full h-64 sm:h-80 md:h-96 lg:h-[420px] object-cover cursor-pointer"
                  onClick={() => handleImageClick('analyzed')}
                />
              ) : (
                <div className="w-full h-64 sm:h-80 md:h-96 lg:h-[420px] bg-gray-800 flex items-center justify-center">
                  <span className="text-gray-400 text-sm sm:text-base">No Analysis Available</span>
                </div>
              )}
            </div>
            <div className="border-t border-gray-700 pt-6">
              {showAnalysis && analysisResult?.data?.predictions?.length > 0 && (
                <div className="flex items-center mb-4 w-full max-w-2xl mx-auto">
                  <button
                    onClick={toggleSearchResults}
                    className="flex items-center hover:bg-gray-800 p-2 rounded transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!analysisResult?.data?.predictions?.length}
                  >
                    <h2 className="text-lg sm:text-xl md:text-2xl font-semibold flex-1 text-left">
                      View Defect Risk Factor Details
                    </h2>
                    <Search className="w-7 h-7 text-white-500 ml-3" />
                  </button>
                </div>
              )}
              {(loading || showAnalysis) && (
                <div className="w-full max-w-2xl mx-auto p-4 bg-gray-900 rounded-lg min-h-[150px] sm:min-h-[200px]">
                  <div className="mt-5 text-gray-300">
                    {analysisResult && (
                      <>
                        {Array.isArray(analysisResult.data?.predictions) && analysisResult.data.predictions.length > 0 ? (
                          <>
                            <ul className="list-disc pl-5">
                              {analysisResult.data.predictions.map((pred, index) => (
                                <li key={index} className="text-gray-300">
                                  <span className="font-medium">The Most Likely Type of Issue:</span>{' '}
                                  {pred.class_name}
                                </li>
                              ))}
                            </ul>
                            <div className="mt-5 text-gray-300">
                              <p>
                                To view detailed possible contributing factors, please click the "View Defect Risk Factor Details" button above. We
                                would appreciate your feedback on any incorrect judgments. For any detected light
                                leakage issue, please refer to the PSS or contact the corresponding TPV
                                representative for criteria confirmation. Note: Light leakage is not acceptable on
                                MNT products.
                              </p>
                            </div>
                          </>
                        ) : (
                          <div className="text-white-500 text-sm sm:text-base">
                            <p> No issue found in the image.</p>
                           
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              {showAnalysis && (
                <div className="flex flex-col items-center gap-3 mt-6 w-full max-w-2xl mx-auto">
                  <button
                    onClick={handleFeedbackClick}
                    className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition text-sm w-full max-w-md disabled:bg-gray-500 disabled:cursor-not-allowed"
                    // disabled={!imageSrc || !analysisResult?.data?.predictions?.length}
                  >
                    Agree Judgment and E-mail Result
                  </button>
                  <button
                    onClick={DisagreeJudgementclick}
                    className="bg-gray-300 text-black px-4 py-2 rounded-lg hover:bg-gray-400 transition text-sm w-full max-w-md"
                    disabled={!showAnalysis}
                  >
                    Disagree Judgment
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {isModalOpen && previewImageSrc && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-3xl w-full">
              <button
                onClick={handleCloseModal}
                className="absolute top-2 right-2 bg-gray-800 text-white p-2 rounded-full hover:bg-gray-700 transition"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={previewImageSrc}
                alt="Preview"
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
            </div>
          </div>
        )}
        {showCameraModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="relative w-full max-w-md bg-gray-900 rounded-lg p-4 flex flex-col gap-4">
              <button
                onClick={handleCloseCameraModal}
                className="absolute top-2 right-2 bg-gray-800 text-white p-2 rounded-full hover:bg-gray-700 transition"
                title="Close Camera"
                aria-label="Close Camera"
              >
                <X className="w-6 h-6" />
              </button>
              <video
                ref={videoRef}
                className="w-full h-64 object-cover rounded-lg"
                autoPlay
                playsInline
              />
              <div className="flex justify-between gap-2">
                <button
                  onClick={handleSwitchCamera}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
                  title="Switch Camera"
                  aria-label="Switch Camera"
                >
                  <SwitchCamera className="w-5 h-5 inline mr-2" />
                  {cameraFacingMode === 'environment' ? 'Front' : 'Back'}
                </button>
                <button
                  onClick={handleCapturePhoto}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm"
                  title="Capture Photo"
                  aria-label="Capture Photo"
                >
                  <Camera className="w-5 h-5 inline mr-2" />
                  Capture
                </button>
              </div>
            </div>
          </div>
        )}

        {showSearchResults && showAnalysis && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-45 p-4 sm:p-6">
            <div className="relative w-full max-w-lg sm:max-w-2xl bg-gray-900 rounded-lg p-4 sm:p-6 flex flex-col gap-4">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <button
                  onClick={handleCloseSearchResults}
                  className="bg-gray-800 text-white p-2 rounded-full hover:bg-gray-700 transition"
                  title="Close Search Results"
                  aria-label="Close Search Results"
                >
                  <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-center flex-1">Defect Risk Probability Ratio</h2>
                <div className="w-10"></div>
              </div>
              <div className="mb-6 sm:mb-8">
                <h3 className="text-base sm:text-lg font-semibold mb-4 text-white">Possibility Defects</h3>
                <div className="flex flex-col">
                  {(analysisResult?.data?.predictions || []).map((issue, index) => (
                    <div key={index} className="flex justify-between text-gray-300 py-2 px-2 sm:px-4">
                      <span>{issue.class_name}</span>
                      <span>{(issue.confidence * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mb-6 sm:mb-8">
                <h3 className="text-base sm:text-lg font-semibold mb-4 text-white">Root Cause Possibility</h3>
                <div className="flex flex-col">
                  <div className="grid grid-cols-2 font-medium text-gray-300 pb-2 mb-2 px-2 sm:px-4">
                    {/* <span>Name</span> */}
                    {/* <span className="text-center">Percentage</span> */}
                  </div>
                  {getRootCausesArray().map((cause, index) => (
                    <div key={index} className="flex justify-between text-gray-300 py-2 px-2 sm:px-4">
                      <span className="text-left">{cause.name}</span>
                      <span className="text-center">{cause.percentage}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-center mt-auto">
                  <button
                    onClick={handleCloseSearchResults}
                    className="bg-gray-800 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition"
                  >
                    Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default UploadImage;